import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  FileText,
  Send,
  Eye,
  Trash2,
  Search,
  Filter,
  MoreVertical,
  PenLine,
  Upload,
  FileUp,
  Download,
  RefreshCw,
  Settings2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { SignaturePad } from '../components/SignaturePad';
import { TemplateFieldEditor } from '../components/TemplateFieldEditor';
import { toast } from 'sonner';

const statusColors = {
  draft: 'status-draft',
  pending_signature: 'status-pending',
  partially_signed: 'status-pending',
  signed: 'status-signed',
  approved: 'status-approved',
  rejected: 'status-rejected',
};

export default function MSAManagement() {
  const { api, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('documents');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [signModalOpen, setSignModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateFields, setTemplateFields] = useState([]);

  // Form states
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    content: '',
    file_data: null,
    file_name: null,
    file_type: null,
  });
  const [uploadMode, setUploadMode] = useState('text'); // 'text' or 'file'
  const [documentForm, setDocumentForm] = useState({
    template_id: '',
    vendor_email: '',
    vendor_name: '',
    vendor_contact: '',
  });

  useEffect(() => {
    fetchData();
    if (searchParams.get('action') === 'new-template') {
      setActiveTab('templates');
      setTemplateModalOpen(true);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, documentsRes, vendorsRes] = await Promise.all([
        api().get('/msa/templates'),
        api().get('/msa/documents'),
        api().get('/vendors'),
      ]);
      setTemplates(templatesRes.data);
      setDocuments(documentsRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to load MSA data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    
    // Show loading state
    const loadingToast = toast.loading('Creating template...');
    
    try {
      const payload = {
        name: templateForm.name,
        description: templateForm.description,
        content: templateForm.content || 'See attached file',
        file_data: templateForm.file_data,
        file_name: templateForm.file_name,
        file_type: templateForm.file_type,
      };
      
      await api().post('/msa/templates', payload);
      toast.success('Template created successfully', { id: loadingToast });
      setTemplateModalOpen(false);
      setTemplateForm({ name: '', description: '', content: '', file_data: null, file_name: null, file_type: null });
      setUploadMode('text');
      fetchData();
    } catch (error) {
      console.error('Template creation error:', error);
      const errorMessage = error.response?.data?.detail || 
        (error.message?.includes('413') ? 'File too large. Try a smaller file.' : 
        (error.message?.includes('Network') ? 'Network error. Check your connection.' : 'Failed to create template'));
      toast.error(errorMessage, { id: loadingToast });
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }
    
    // Show loading state
    toast.loading('Reading file...', { id: 'file-upload' });
    
    const reader = new FileReader();
    reader.onload = () => {
      setTemplateForm({
        ...templateForm,
        file_data: reader.result,
        file_name: file.name,
        file_type: file.type || 'application/octet-stream',
      });
      toast.success('File loaded successfully', { id: 'file-upload' });
    };
    reader.onerror = () => {
      toast.error('Failed to read file', { id: 'file-upload' });
    };
    reader.readAsDataURL(file);
  };

  const handleDownloadTemplate = async (templateId, fileName) => {
    try {
      const response = await api().get(`/msa/templates/${templateId}/download`);
      const { file_data, file_name } = response.data;
      
      const link = document.createElement('a');
      link.href = file_data;
      link.download = file_name || fileName || 'template';
      link.click();
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await api().delete(`/msa/templates/${templateId}`);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    try {
      await api().post('/msa/documents', documentForm);
      toast.success('MSA document created');
      setDocumentModalOpen(false);
      setDocumentForm({ template_id: '', vendor_email: '', vendor_name: '', vendor_contact: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create document');
    }
  };

  const handleSendForSignature = async (documentId) => {
    try {
      await api().post(`/msa/documents/${documentId}/send`);
      toast.success('Document sent for signature');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send document');
    }
  };

  const handleResendSigningLink = async (documentId) => {
    try {
      await api().post(`/msa/documents/${documentId}/resend`);
      toast.success('Signing link resent');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend signing link');
    }
  };

  const handleSign = async (signatureData) => {
    try {
      await api().post('/signatures', {
        document_id: selectedDocument.id,
        document_type: 'msa',
        ...signatureData,
      });
      toast.success('Document signed successfully');
      setSignModalOpen(false);
      setSelectedDocument(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign document');
    }
  };

  const openFieldEditor = async (template) => {
    setSelectedTemplate(template);
    try {
      const response = await api().get(`/msa/templates/${template.id}/fields`);
      setTemplateFields(response.data);
    } catch (error) {
      setTemplateFields([]);
    }
    setFieldEditorOpen(true);
  };

  const handleSaveFields = async (fields) => {
    const fieldsToSave = fields.map(f => ({
      field_type: f.field_type,
      field_name: f.field_name,
      role: f.role,
      position_x: f.position_x,
      position_y: f.position_y,
      page_number: f.page_number,
      width: f.width,
      height: f.height,
    }));
    
    await api().post(`/msa/templates/${selectedTemplate.id}/fields/bulk`, fieldsToSave);
    fetchData();
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      doc.vendor_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.id.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const canSign = user?.role === 'admin' || user?.role === 'approver';
  const canSend = user?.role === 'admin' || user?.role === 'sender';

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="msa-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Manrope'] tracking-tight">
            MSA Management
          </h1>
          <p className="text-zinc-400 mt-1">
            Create and manage Master Service Agreements
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger
              value="documents"
              className="data-[state=active]:bg-zinc-800"
              data-testid="tab-documents"
            >
              Documents ({documents.length})
            </TabsTrigger>
            <TabsTrigger
              value="templates"
              className="data-[state=active]:bg-zinc-800"
              data-testid="tab-templates"
            >
              Templates ({templates.length})
            </TabsTrigger>
          </TabsList>

          {activeTab === 'documents' ? (
            <Dialog open={documentModalOpen} onOpenChange={setDocumentModalOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-blue-600 hover:bg-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]"
                  data-testid="new-document-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New MSA Document
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-white">Create MSA Document</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Select a template and vendor to create a new MSA
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateDocument} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Template</Label>
                    <Select
                      value={documentForm.template_id}
                      onValueChange={(v) =>
                        setDocumentForm({ ...documentForm, template_id: v })
                      }
                    >
                      <SelectTrigger
                        className="bg-zinc-950 border-zinc-800"
                        data-testid="template-select"
                      >
                        <SelectValue placeholder="Select template" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Vendor Company Name</Label>
                    <Input
                      value={documentForm.vendor_name}
                      onChange={(e) =>
                        setDocumentForm({ ...documentForm, vendor_name: e.target.value })
                      }
                      placeholder="Vendor Company Inc."
                      className="bg-zinc-950 border-zinc-800"
                      required
                      data-testid="vendor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Vendor Contact Name</Label>
                    <Input
                      value={documentForm.vendor_contact}
                      onChange={(e) =>
                        setDocumentForm({ ...documentForm, vendor_contact: e.target.value })
                      }
                      placeholder="John Smith"
                      className="bg-zinc-950 border-zinc-800"
                      required
                      data-testid="vendor-contact-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Vendor Email</Label>
                    <Input
                      type="email"
                      value={documentForm.vendor_email}
                      onChange={(e) =>
                        setDocumentForm({ ...documentForm, vendor_email: e.target.value })
                      }
                      placeholder="vendor@company.com"
                      className="bg-zinc-950 border-zinc-800"
                      required
                      data-testid="vendor-email-input"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDocumentModalOpen(false)}
                      className="border-zinc-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-500"
                      disabled={!documentForm.template_id || !documentForm.vendor_email || !documentForm.vendor_name || !documentForm.vendor_contact}
                      data-testid="create-document-btn"
                    >
                      Create Document
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={templateModalOpen} onOpenChange={setTemplateModalOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-blue-600 hover:bg-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]"
                  data-testid="new-template-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-white">Create MSA Template</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Create a reusable template for your MSAs
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateTemplate} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Template Name</Label>
                    <Input
                      value={templateForm.name}
                      onChange={(e) =>
                        setTemplateForm({ ...templateForm, name: e.target.value })
                      }
                      placeholder="e.g., Standard MSA 2024"
                      className="bg-zinc-950 border-zinc-800"
                      required
                      data-testid="template-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Description</Label>
                    <Input
                      value={templateForm.description}
                      onChange={(e) =>
                        setTemplateForm({ ...templateForm, description: e.target.value })
                      }
                      placeholder="Brief description of this template"
                      className="bg-zinc-950 border-zinc-800"
                      data-testid="template-description-input"
                    />
                  </div>
                  
                  {/* Upload Mode Toggle */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Template Content</Label>
                    <div className="flex gap-2 mb-3">
                      <Button
                        type="button"
                        variant={uploadMode === 'text' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setUploadMode('text')}
                        className={uploadMode === 'text' ? 'bg-blue-600' : 'border-zinc-700'}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Type Content
                      </Button>
                      <Button
                        type="button"
                        variant={uploadMode === 'file' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setUploadMode('file')}
                        className={uploadMode === 'file' ? 'bg-blue-600' : 'border-zinc-700'}
                      >
                        <FileUp className="w-4 h-4 mr-2" />
                        Upload File
                      </Button>
                    </div>
                    
                    {uploadMode === 'text' ? (
                      <Textarea
                        value={templateForm.content}
                        onChange={(e) =>
                          setTemplateForm({ ...templateForm, content: e.target.value })
                        }
                        placeholder="Enter your MSA template content..."
                        className="bg-zinc-950 border-zinc-800 min-h-[200px] font-mono text-sm"
                        required={uploadMode === 'text'}
                        data-testid="template-content-input"
                      />
                    ) : (
                      <div className="space-y-3">
                        <div className="border-2 border-dashed border-zinc-700 rounded-lg p-6 text-center hover:border-zinc-600 transition-colors">
                          <input
                            type="file"
                            id="msa-template-file"
                            accept=".pdf,.doc,.docx,.txt"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <label htmlFor="msa-template-file" className="cursor-pointer">
                            <Upload className="w-10 h-10 mx-auto text-zinc-500 mb-3" />
                            <p className="text-zinc-300 font-medium">Click to upload MSA template</p>
                            <p className="text-zinc-500 text-sm mt-1">PDF, DOC, DOCX, or TXT</p>
                          </label>
                        </div>
                        {templateForm.file_name && (
                          <div className="flex items-center gap-2 p-3 bg-zinc-800/50 rounded-lg">
                            <FileText className="w-5 h-5 text-blue-400" />
                            <span className="text-zinc-300 text-sm flex-1">{templateForm.file_name}</span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setTemplateForm({ ...templateForm, file_data: null, file_name: null, file_type: null })}
                              className="text-zinc-400 hover:text-rose-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTemplateModalOpen(false)}
                      className="border-zinc-700"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-blue-600 hover:bg-blue-500"
                      disabled={!templateForm.name || (uploadMode === 'text' && !templateForm.content) || (uploadMode === 'file' && !templateForm.file_data)}
                      data-testid="create-template-btn"
                    >
                      Create Template
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-zinc-900 border-zinc-800"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_signature">Pending Signature</SelectItem>
                <SelectItem value="signed">Signed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Documents Table */}
          <Card className="bg-zinc-900/40 border-zinc-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Signatures</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.length > 0 ? (
                    filteredDocuments.map((doc) => (
                      <tr key={doc.id} data-testid={`msa-doc-${doc.id}`}>
                        <td>
                          <div>
                            <p className="font-medium text-zinc-200">
                              {doc.vendor_name || 'Unknown Vendor'}
                            </p>
                            <p className="text-xs text-zinc-500">{doc.vendor_email}</p>
                          </div>
                        </td>
                        <td>
                          <Badge
                            className={`${statusColors[doc.status]} capitalize`}
                          >
                            {doc.status.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="text-zinc-400">
                          {new Date(doc.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <span className="text-zinc-400">
                            {doc.signatures?.length || 0} signature(s)
                          </span>
                        </td>
                        <td className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="bg-zinc-900 border-zinc-800"
                            >
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedDocument(doc);
                                  setViewModalOpen(true);
                                }}
                                className="text-zinc-300"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                View
                              </DropdownMenuItem>
                              {canSend && doc.status === 'draft' && (
                                <DropdownMenuItem
                                  onClick={() => handleSendForSignature(doc.id)}
                                  className="text-zinc-300"
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  Send for Signature
                                </DropdownMenuItem>
                              )}
                              {canSend && doc.status === 'pending_signature' && (
                                <DropdownMenuItem
                                  onClick={() => handleResendSigningLink(doc.id)}
                                  className="text-zinc-300"
                                >
                                  <RefreshCw className="w-4 h-4 mr-2" />
                                  Resend Signing Link
                                </DropdownMenuItem>
                              )}
                              {canSign && doc.status === 'pending_signature' && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setSelectedDocument(doc);
                                    setSignModalOpen(true);
                                  }}
                                  className="text-zinc-300"
                                >
                                  <PenLine className="w-4 h-4 mr-2" />
                                  Sign
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>
                        <div className="empty-state">
                          <FileText className="empty-state-icon" />
                          <p className="text-zinc-400">No MSA documents found</p>
                          <p className="text-zinc-500 text-sm">
                            Create your first MSA document to get started
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.length > 0 ? (
              templates.map((template) => (
                <Card
                  key={template.id}
                  className="bg-zinc-900/40 border-zinc-800 card-hover"
                  data-testid={`template-${template.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg text-zinc-200">
                          {template.name}
                        </CardTitle>
                        <CardDescription className="text-zinc-500">
                          {template.description || 'No description'}
                        </CardDescription>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="bg-zinc-900 border-zinc-800"
                        >
                          {template.has_file && (
                            <>
                              <DropdownMenuItem
                                onClick={() => openFieldEditor(template)}
                                className="text-blue-400"
                              >
                                <Settings2 className="w-4 h-4 mr-2" />
                                Map Fields
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDownloadTemplate(template.id, template.file_name)}
                                className="text-zinc-300"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download File
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="text-rose-400"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-zinc-500">
                        Created {new Date(template.created_at).toLocaleDateString()}
                      </p>
                      {template.has_file && (
                        <Badge className="bg-blue-500/10 text-blue-400 border border-blue-500/20">
                          <FileUp className="w-3 h-3 mr-1" />
                          File Attached
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="col-span-full bg-zinc-900/40 border-zinc-800">
                <CardContent>
                  <div className="empty-state">
                    <FileText className="empty-state-icon" />
                    <p className="text-zinc-400">No templates yet</p>
                    <p className="text-zinc-500 text-sm">
                      Create your first MSA template to get started
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* View Document Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">MSA Document</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Vendor: {selectedDocument?.vendor_name}
            </DialogDescription>
          </DialogHeader>
          <div className="document-preview">{selectedDocument?.content}</div>
        </DialogContent>
      </Dialog>

      {/* Sign Document Modal */}
      <Dialog open={signModalOpen} onOpenChange={setSignModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Sign Document</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add your electronic signature to this MSA
            </DialogDescription>
          </DialogHeader>
          <SignaturePad
            onSave={handleSign}
            onCancel={() => setSignModalOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Field Editor Dialog */}
      <Dialog open={fieldEditorOpen} onOpenChange={setFieldEditorOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-[95vw] h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>Map Fields on Template</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <TemplateFieldEditor
              templateId={selectedTemplate.id}
              fileData={`${process.env.REACT_APP_BACKEND_URL}/api/msa/templates/${selectedTemplate.id}/download`}
              fileName={selectedTemplate.file_name || selectedTemplate.name}
              existingFields={templateFields}
              onSave={handleSaveFields}
              onClose={() => setFieldEditorOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
